import { Component, EventEmitter, Input, Output, ViewChild, OnInit, OnChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';

import { DynamicFormComponent, QuestionBase } from 'core/components/forms';
import { Client } from 'core/models/client';
import { Course } from 'core/models/course';
import { Itinerary } from 'core/models/itinerary';
import { ClientsService } from 'core/services/clients.service';
import { XlsxService } from 'core/services/xlsx.service';
import { environment } from 'environments/environment';
import { SsoConfigurationsService } from '../../services/sso-configurations.service';
import { ClientSsoQuestionsService } from '../../questions-services/client-sso-questions.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lms-client-sso-editor',
  templateUrl: './client-sso-editor.component.html',
  styleUrls: ['./client-sso-editor.component.scss'],
  providers: [ClientSsoQuestionsService, SsoConfigurationsService]
})
export class ClientSsoEditorComponent implements OnInit, OnChanges {
  @Input() client: Client;
  @Input() sid: string;
  @Input() ssoConfig: any;
  @Output() saved = new EventEmitter<Client>();
  @ViewChild('ssoConfigForm', { static: false }) public ssoConfigForm: DynamicFormComponent;
  public questions: QuestionBase<any>[];
  public ssoUrl: string;
  public courses: Course[];
  public itineraries: Itinerary[];
  public tempCourses: Course[];
  public tempItineraries: Itinerary[];
  public showDeepLinkList = false;
  public deepLinkListType: string = 'home';
  public isCampusSso: boolean = false;

  public listTypes = [
    {
      key: 'home',
      value: this.translateService.instant('clients.config.sso.accessHome')
    },
    {
      key: 'course',
      value: this.translateService.instant('clients.config.sso.accessCourse')
    },
    {
      key: 'game',
      value: this.translateService.instant('clients.config.sso.accessGame')
    }
  ];

  public columns = [
    {
      column: 'name',
      type: String,
      value: (item) => item.name,
      width: 50
    },
    {
      column: 'url',
      type: String,
      value: (item) => item.url,
      width: 100
    }
  ];

  private isScormClient: boolean;

  constructor(
    public ssoQuestionsService: ClientSsoQuestionsService,
    public ssoConfigurationsService: SsoConfigurationsService,
    public clientsService: ClientsService,
    public translateService: TranslateService,
    public xlsxService: XlsxService,
    public route: ActivatedRoute,
    public clipboard: Clipboard
  ) {}

  ngOnInit() {
    this.setSsoUrl();
    this.isScormClient = !!this.client.scorm?.enabled;
    this.route.data.subscribe((data) => {
      this.courses = data['courses'].courses;
      this.itineraries = data['itineraries'].itineraries;
    });
    this.tempCourses = [...this.courses];
    this.tempItineraries = [...this.itineraries];
  }

  ngOnChanges() {
    this.setQuestions();
  }

  private setQuestions() {
    this.setSsoUrl();
    const { id: clientId, name: clientName } = this.client;
    const type = environment.sids.campus !== this.sid ? 'saml' : this.ssoConfig?.type;
    const ssoConfig = { enabled: false, type, clientId, clientName, ...this.ssoConfig };
    this.questions = this.ssoQuestionsService.getQuestions(this.sid, ssoConfig, this.isScormClient);
    this.isCampusSso = environment.sids.campus === this.sid;
  }

  private setSsoUrl() {
    // todo cambiar a /auth/sso
    this.ssoUrl = this.ssoConfig ? `${environment.API_URL}/sso/${this.ssoConfig.uuid}` : '';
  }

  public changeDeepLinkType(type: string) {
    this.deepLinkListType = type;
  }

  public getFullSsoUrl(uuid: string): string {
    return `${this.ssoUrl}/deep/${uuid}${this.deepLinkListType === 'game' ? '?launch=true' : ''}`;
  }

  public searchWords(words: string) {
    const { courses, itineraries } = this.search(words);
    this.tempCourses = courses;
    this.tempItineraries = itineraries;
  }

  private search(words: string): any {
    let tempCourses: Course[] = [];
    let tempItineraries: Itinerary[] = [];
    if (!words) {
      tempCourses = [...this.courses];
      tempItineraries = [...this.itineraries];
    } else {
      tempCourses = this.courses.filter(
        (course: Course) =>
          (course.name && course.name.toLowerCase().includes(words.toLowerCase())) ||
          (course.uuid && course.uuid.toLowerCase().includes(words.toLowerCase()))
      );
      tempItineraries = this.itineraries.filter(
        (itinerary: Itinerary) =>
          (itinerary.name && itinerary.name.toLowerCase().includes(words.toLowerCase())) ||
          (itinerary.uuid && itinerary.uuid.toLowerCase().includes(words.toLowerCase()))
      );
    }
    return { courses: tempCourses, itineraries: tempItineraries };
  }

  public copyToClipboard(url: string) {
    this.clipboard.copy(url);
  }

  public submit(ssoConfig: any) {
    this.ssoConfigurationsService
      .updateSsoConfig(this.client.id, ssoConfig)
      .then((updatedSsoConfig) => {
        this.ssoConfig = updatedSsoConfig;
        this.setSsoUrl();
        this.saved.emit(updatedSsoConfig);
        this.endSubmit(null);
      })
      .catch((err) => {
        this.endSubmit(err);
      });
  }

  private endSubmit(value) {
    this.ssoConfigForm.submitted(value);
  }
  private buildData() {
    const itineraries = this.getContent(this.tempItineraries);
    const courses = this.getContent(this.tempCourses);
    return [
      {
        sheet: this.translateService.instant('courses.title'),
        schema: this.columns,
        content: courses
      },
      {
        sheet: this.translateService.instant('itineraries.title'),
        schema: this.columns,
        content: itineraries
      }
    ];
  }

  private getContent(items: any[]) {
    return items.map((item: any) => {
      return {
        name: item.name,
        url: this.getFullSsoUrl(item.uuid)
      };
    });
  }
  public downloadLinksXlsx() {
    const data = this.buildData();
    const filename = `${this.client.name}_${this.deepLinkListType === 'game' ? 'course' : 'game'}Links`;

    this.xlsxService.dataToXlsx(data, filename); // Will download the excel file
  }
}
